/**
 * <FeatureGate> — the canonical, fail-closed release-toggle gate.
 *
 * Renders `children` ONLY when its release toggle `feature` key is enabled in
 * the server-resolved set bootstrapped in `releaseFeatures.ts`; otherwise it
 * renders `fallback` (default `null`). Built on the `useFeature` hook, so an
 * unknown key, a not-yet-bootstrapped state, or a failed bootstrap all hide the
 * children (fail-closed, SPEC-FEATURE-TOGGLES-RULE-001/006).
 *
 * This component SUPERSEDES the legacy fail-open gates:
 *   - web/src/components/shared/FeatureGate.tsx (per-workspace fetch, 3 fail-open points)
 *   - web/src/components/shared/FeatureVisibilityGate.tsx (build-time static config)
 * Those files are repointed to re-export this one (CHG-008/009).
 *
 * The `placeholder`/`fallback` prop superset matches the two legacy components
 * so existing consumers (RolesPage, sidebars) keep their call sites unchanged.
 *
 * @module feature-gate/FeatureGate
 * @see SPEC-FEATURE-TOGGLES
 */

import React from "react";
import { useFeature } from "./useFeature";

export interface FeatureGateProps {
  /** Canonical dot-separated release-toggle key (e.g. "agent.schedules"). */
  feature: string;
  /** Rendered when the feature is enabled. */
  children: React.ReactNode;
  /**
   * Rendered when the feature is disabled / unknown / not yet resolved.
   * Defaults to `null` (render nothing). Fail-closed: there is no path that
   * renders `children` on an unresolved key.
   */
  fallback?: React.ReactNode;
  /**
   * Backward-compat alias for `fallback`, accepted because the legacy
   * FeatureGate exposed a separate `placeholder` (shown during its async load).
   * The new gate has no per-component async state, so `placeholder` is treated
   * as `fallback` when `fallback` is not given.
   */
  placeholder?: React.ReactNode;
}

export const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  children,
  fallback = null,
  placeholder,
}) => {
  const enabled = useFeature(feature);
  if (enabled) return <>{children}</>;
  return <>{fallback ?? placeholder ?? null}</>;
};

export default FeatureGate;
